import { NodeType } from "./LogParser.Types";
// #region ANSII section
const ESC = "\u001b";
const TimestampLength = 29;
export const TimestampRegex = /^.{27}Z /gm;
export const BrightClassPostfix = " bright";
/**
 * Regex to identify the parsed lines
 */
export const RegexToIdentify = /\u200C/;
/**
 * Regex for matching ANSII escape codes
 * \u001b - ESC character
 * ?: Non-capturing group
 * (?:\u001b[) : Match ESC[
 * (?:[\?|#])??: Match also ? and # formats that we don't supports but want to eat our special characters to get rid of ESC character
 * (?:[0-9]{1,3})?: Match one or more occurances of the simple format we want with out semicolon
 * (?:(?:;[0-9]{0,3})*)?: Match one or more occurances of the format we want with semicolon
 */
const _ansiEscapeCodeRegex = /(?:\u001b\[)(?:[\?|#])?(?:(?:[0-9]{1,3})?(?:(?:;[0-9]{0,3})*)?[A-Z|a-z])/;
export const ansiEscapeCodeRegexString = "(?:\u001b\\[)(?:[\?|#])?(?:(?:[0-9]{1,3})?(?:(?:;[0-9]{0,3})*)?[A-Z|a-z])";
/**
 * http://ascii-table.com/ansi-escape-sequences.php
 * https://en.wikipedia.org/wiki/ANSI_escape_code#3/4_bit
 * Currently https://en.wikipedia.org/wiki/ANSI_escape_code#8-bit  is NOT supported
 * We support sequences of format:
 *  Esc[CONTENTHEREm
 *  Where CONTENTHERE can be of format: VALUE;VALUE;VALUE or VALUE
 *      Where VALUE is SGR parameter https://www.vt100.net/docs/vt510-rm/SGR
 *          We support: 0 (reset), 1 (bold), 3 (italic), 4 (underline), 22 (not bold), 23 (not italic), 24 (not underline), 39 (default fg), 49 (default bg),
 *                      fg colors - 30 (black), 31 (red), 32 (green), 33 (yellow), 34 (blue), 35 (magenta), 36 (cyan), 37 (white), 90 (grey)
 *                        with more brighness - 91 (red), 92 (green), 93 (yellow), 94 (blue), 95 (magenta), 96 (cyan), 97 (white)
 *                      bg colors - 40 (black), 41 (red), 42 (green), 43 (yellow), 44 (blue), 45 (magenta), 46 (cyan), 47 (white), 100 (grey)
 *                        with more brighness- 101 (red), 102 (green), 103 (yellow), 104 (blue), 105 (magenta), 106 (cyan), 107 (white)
 *  Where m refers to the "Graphics mode"
 */
// #endregion ANSII section
/**
 * Regex for matching URLs in the console logs
 * https?               : Match http and https
 * \:\/\/               : Match ://
 * [[^\]\):\s]*         : Match any symbol besides whitespace, ], ), or :
 * ([\]\):](?!\s))*     : Match ], ), or : if not followed by space
 */
const _urlRegex = /https?\:\/\/([^\]\):\s]*([\]\):](?!\s))*)+/;
// #region Pipelines commands
var Resets;
(function (Resets) {
    Resets["Reset"] = "0";
    Resets["Bold"] = "22";
    Resets["Italic"] = "23";
    Resets["Underline"] = "24";
    Resets["Fg"] = "39";
    Resets["Bg"] = "49";
})(Resets || (Resets = {}));
const specials = {
    "1": "bold",
    "3": "italic",
    "4": "underline"
};
export const bgColors = {
    // 40 (black), 41 (red), 42 (green), 43 (yellow), 44 (blue), 45 (magenta), 46 (cyan), 47 (white), 100 (grey)
    "40": "b",
    "41": "r",
    "42": "g",
    "43": "y",
    "44": "bl",
    "45": "m",
    "46": "c",
    "47": "w",
    "100": "gr"
};
export const fgColors = {
    // 30 (black), 31 (red), 32 (green), 33 (yellow), 34 (blue), 35 (magenta), 36 (cyan), 37 (white), 90 (grey)
    "30": "b",
    "31": "r",
    "32": "g",
    "33": "y",
    "34": "bl",
    "35": "m",
    "36": "c",
    "37": "w",
    "90": "gr"
};
export const plain = "plain";
export const command = "command";
export const debug = "debug";
export const error = "error";
export const info = "info";
export const section = "section";
export const verbose = "verbose";
export const warning = "warning";
export const group = "group";
export const endGroup = "endgroup";
export const icon = "icon";
export const commandToType = {
    "command": NodeType.Command,
    "debug": NodeType.Debug,
    "error": NodeType.Error,
    "info": NodeType.Info,
    "section": NodeType.Section,
    "verbose": NodeType.Verbose,
    "warning": NodeType.Warning,
    "group": NodeType.Group,
    "endgroup": NodeType.EndGroup,
    "icon": NodeType.Icon
};
export var RenderType;
(function (RenderType) {
    RenderType[RenderType["Group"] = 0] = "Group";
    RenderType[RenderType["Icon"] = 1] = "Icon";
})(RenderType || (RenderType = {}));
export const commandToRenderType = {
    "group": RenderType.Group,
    "icon": RenderType.Icon
};
export const typeToCommand = {
    "0": plain,
    "1": command,
    "2": debug,
    "3": error,
    "4": info,
    "5": section,
    "6": verbose,
    "7": warning,
    "8": group,
    "9": endGroup,
    "10": icon
};
// Store the max command length we support, for example, we support "section", "command" which are of length 7, which highest of all others
const maxCommandLength = 8;
const supportedCommands = [command, debug, error, info, section, verbose, warning, group, endGroup, icon];
export function getType(node) {
    return typeToCommand[node.type];
}
// #endregion Pipelines commands
// #region Common functions
//https://www.owasp.org/index.php/XSS_(Cross_Site_Scripting)_Prevention_Cheat_Sheet#RULE_.231_-_HTML_Escape_Before_Inserting_Untrusted_Data_into_HTML_Element_Content
export function escape(content) {
    let result = "";
    content = content || "";
    for (let index = 0; index < content.length; index++) {
        const char = content.charAt(index);
        if (char === "&") {
            result += "&amp;";
        }
        else if (char === "<") {
            result += "&lt;";
        }
        else if (char === ">") {
            result += "&gt;";
        }
        else if (char === "\"") {
            result += "&quot;";
        }
        else if (char === "'") {
            result += "&#x27;";
        }
        else if (char === "/") {
            result += "&#x2F;";
        }
        else {
            result += char;
        }
    }
    return result;
}
export function getText(text) {
    return (text || "").toLocaleLowerCase();
}
// Set max to prevent any perf degradations
const maxLineMatchesToParse = 100;
const maxMatches = 50;
const unsetValue = -1;
const newLineChar = "\n";
const carriageReturnNewLineChar = "\r\n";
const hashChar = "#";
const commandStart = "[";
const commandEnd = "]";
export class Parser {
    /**
     * Converts the content to HTML with appropriate styles, escapes content to prevent XSS
     * @param content
     * @param lineNumber
     * @param findResult
     * @param dangerouslyDontEscape If set, we will skip escaping HTML, this should be only used for internal content and make sure XSS isn't possible with the content you are injecting
     * @param id
     */
    parse(content, lineNumber, findResult, dangerouslyDontEscape, id) {
        let result = "";
        const states = this.getStates(content);
        states.forEach(x => {
            let classNames = "";
            const currentText = x.output;
            if (x.style) {
                let fg = x.style.fg;
                let bg = x.style.bg;
                if (fg) {
                    classNames += `ansifg-${fg} `;
                }
                if (bg) {
                    classNames += `ansibg-${bg} `;
                }
                if (x.style.bold) {
                    classNames += "fontWeightHeavy ";
                }
                if (x.style.italic) {
                    classNames += " italic";
                }
                if (x.style.underline) {
                    classNames += " underline";
                }
            }
            let start = "<span";
            if (classNames) {
                start += ` class='${classNames}'`;
            }
            if (id) {
                start += ` id='${id}'`;
            }
            start += ">";
            let output = "";
            let matchIndex = -1;
            let prevMatchIndex = 0;
            let lines = [];
            if (findResult) {
                const selectedLine = findResult.selectedLine;
                let selectedLineIsIncluded = false;
                // slice to not degrade perf
                findResult.lines.slice(0, maxLineMatchesToParse).forEach(x => {
                    if (x.line === selectedLine) {
                        selectedLineIsIncluded = true;
                    }
                    lines.push(x.line);
                });
                if (selectedLine && !selectedLineIsIncluded) {
                    // make sure we always include selected line
                    lines.push(findResult.selectedLine);
                }
            }
            if (findResult && lines.indexOf(lineNumber) !== -1) {
                const textToFind = findResult.text;
                /* tslint:disable no-conditional-assignment*/
                let matchCount = 1;
                const textContent = getText(currentText);
                while ((matchIndex = textContent.indexOf(getText(textToFind), matchIndex + 1)) !== -1) {
                    if (matchCount >= maxMatches) {
                        break;
                    }
                    // unmatched before match
                    const preUnMatch = currentText.substring(prevMatchIndex, matchIndex);
                    if (preUnMatch) {
                        output += this._escape(preUnMatch, false);
                    }
                    prevMatchIndex = matchIndex + textToFind.length;
                    // actual match
                    const markerText = currentText.substring(matchIndex, prevMatchIndex);
                    output += `<span class="dt-fm ${lineNumber === findResult.selectedLine ? "select" : ""}">` + this._escape(markerText, !!dangerouslyDontEscape) + "</span>";
                    matchCount++;
                }
                /* tslint:enable no-conditional-assignment*/
                // rest of the unmatched content post all matches
                if (prevMatchIndex > 0 && currentText.length > prevMatchIndex) {
                    const data = currentText.substring(prevMatchIndex, currentText.length);
                    output += this._escape(data, false);
                }
                // simple case, nothing matched
                if (matchCount === 1) {
                    output = this._escape(currentText, true);
                }
            }
            else {
                output = this._getLink(currentText, dangerouslyDontEscape);
            }
            result += start + output + "</span>";
        });
        return result;
    }
    /**
     * Parses the content into lines with nodes
     * @param content content to parse
     */
    parseLines(content, startIndex, oldLinesLength) {
        // lines we return
        let lines = [];
        // accumulated nodes for a particular line
        let nodes = [];
        // index in content where we start parsing
        let startParseIndex = startIndex || 0;
        // start of a particular line
        let lineStartIndex = startParseIndex;
        // start of plain node content
        let plainNodeStart = unsetValue;
        // tells to consider the default logic where we check for plain text etc.,
        let considerDefaultLogic = true;
        // stores the command, to match one of the 'supportedCommands'
        let currentCommand = "";
        // helps in finding commands in our format "##[command]" or "[command]"
        let commandSeeker = "";
        // when line ends, this tells if there's any pending node
        let pendingLastNode = unsetValue;
        const resetCommandVar = () => {
            commandSeeker = "";
            currentCommand = "";
        };
        const resetPlain = () => {
            plainNodeStart = unsetValue;
        };
        const resetPending = () => {
            pendingLastNode = unsetValue;
        };
        const parseCommandEnd = () => {
            // possible continuation of our well-known commands
            const commandIndex = supportedCommands.indexOf(currentCommand);
            if (commandIndex !== -1) {
                considerDefaultLogic = false;
                // we reached the end and found the command
                resetPlain();
                // command is for the whole line, so we are not pushing the node here but defering this to when we find the new line
                pendingLastNode = commandToType[currentCommand];
                if (currentCommand === section || currentCommand === group || currentCommand === endGroup || currentCommand === command) {
                    // strip off ##[$(currentCommand)] if there are no timestamps at start
                    const possibleTimestamp = content.substring(lineStartIndex, lineStartIndex + TimestampLength) || "";
                    if (!possibleTimestamp.match(TimestampRegex)) {
                        // ## is optional, so pick the right offfset
                        const offset = content.substring(lineStartIndex, lineStartIndex + 2) === "##" ? 4 : 2;
                        lineStartIndex = lineStartIndex + offset + currentCommand.length;
                    }
                }
                if (currentCommand === group) {
                    groupStarted = true;
                }
                // group logic- happyCase1: we found endGroup and there's already a group starting
                if (currentCommand === endGroup && currentGroupNode) {
                    groupEnded = true;
                }
            }
            resetCommandVar();
        };
        let groupStarted = false;
        let groupEnded = false;
        let currentGroupNode;
        let nodeIndex = oldLinesLength || 0;
        if (content.charAt(startParseIndex) === newLineChar) {
            startParseIndex += 1;
            lineStartIndex += 1;
        }
        for (let index = startParseIndex; index < content.length; index++) {
            const char = content.charAt(index);
            // start with considering default logic, individual conditions are responsible to set it false
            considerDefaultLogic = true;
            if (char === newLineChar || index === content.length - 1) {
                if (char === commandEnd) {
                    parseCommandEnd();
                }
                let node = {
                    type: pendingLastNode,
                    start: lineStartIndex,
                    end: index,
                    lineIndex: lines.length + (oldLinesLength || 0)
                };
                let pushNode = false;
                // end of the line/text, push any final nodes
                if (pendingLastNode !== NodeType.Plain) {
                    // there's pending special node to be pushed
                    pushNode = true;
                    // a new group has just started
                    if (groupStarted) {
                        currentGroupNode = node;
                        groupStarted = false;
                    }
                    // a group has ended
                    if (groupEnded && currentGroupNode) {
                        // this is a throw away node
                        pushNode = false;
                        currentGroupNode.isGroup = true;
                        // since group has ended, clear all of our pointers
                        groupEnded = false;
                        currentGroupNode = undefined;
                    }
                }
                else if (pendingLastNode === NodeType.Plain) {
                    // there's pending plain node to be pushed
                    pushNode = true;
                }
                if (pushNode) {
                    node.index = nodeIndex++;
                    nodes.push(node);
                }
                // A group is pending
                if (currentGroupNode && node !== currentGroupNode) {
                    node.group = {
                        lineIndex: currentGroupNode.lineIndex,
                        nodeIndex: currentGroupNode.index
                    };
                }
                // end of the line, push all nodes that are accumulated till now
                const lineMumber = lines.length + 1 + (oldLinesLength || 0);
                if (nodes.length > 0) {
                    lines.push({
                        nodes: nodes,
                        lineNumber: lineMumber
                    });
                }
                // clear node as we are done with the line
                nodes = [];
                // increment lineStart for the next line
                lineStartIndex = index + 1;
                // unset
                resetPlain();
                resetPending();
                resetCommandVar();
                considerDefaultLogic = false;
            }
            else if (char === hashChar) {
                // possible start of our well-known commands
                if (commandSeeker === "" || commandSeeker === "#") {
                    considerDefaultLogic = false;
                    commandSeeker += hashChar;
                }
            }
            else if (char === commandStart) {
                // possible continuation of our well-known commands
                if (commandSeeker === "##") {
                    considerDefaultLogic = false;
                    commandSeeker += commandStart;
                }
                else if (commandSeeker.length === 0) {
                    // covers - "", for live logs, commands will be of [section], with out "##"
                    considerDefaultLogic = false;
                    commandSeeker += commandStart;
                }
            }
            else if (char === commandEnd) {
                if (currentCommand === icon) {
                    let iconStartIndex = index + 1;
                    let endIndex = iconStartIndex;
                    for (let i = iconStartIndex; i < content.length; i++) {
                        const iconChar = content[i];
                        if (iconChar === " ") {
                            endIndex = i;
                            break;
                        }
                    }
                    nodes.push({
                        type: NodeType.Icon,
                        lineIndex: lines.length,
                        start: iconStartIndex,
                        end: endIndex,
                        index: nodeIndex++
                    });
                    // jump to post Icon content
                    index = endIndex + 1;
                    lineStartIndex = index;
                    continue;
                }
                else {
                    parseCommandEnd();
                }
            }
            if (considerDefaultLogic) {
                if (commandSeeker === "##[" || commandSeeker === "[") {
                    // it's possible that we are parsing a command
                    currentCommand += char.toLowerCase();
                }
                if (currentCommand.length > maxCommandLength) {
                    // to avoid accumulating command unncessarily, let's check max length, if it exceeds, it's not a command
                    resetCommandVar();
                }
                // considering as plain text
                if (plainNodeStart === unsetValue) {
                    // we didn't set this yet, set now
                    plainNodeStart = lineStartIndex;
                    // set pending node if there isn't one pending
                    if (pendingLastNode === unsetValue) {
                        pendingLastNode = NodeType.Plain;
                    }
                }
            }
        }
        return lines;
    }
    /**
     * Parses the content into ANSII states
     * @param content content to parse
     */
    getStates(content) {
        let result = [];
        // Eg: "ESC[0KESC[33;1mWorker informationESC[0m
        if (!_ansiEscapeCodeRegex.test(content)) {
            // Not of interest, don't touch content
            return [{
                    output: content
                }];
        }
        let command = "";
        let currentText = "";
        let code = "";
        let state = {};
        let isCommandActive = false;
        let codes = [];
        for (let index = 0; index < content.length; index++) {
            const character = content[index];
            if (isCommandActive) {
                if (character === ";") {
                    if (code) {
                        codes.push(code);
                        code = "";
                    }
                }
                else if (character === "m") {
                    if (code) {
                        isCommandActive = false;
                        // done
                        codes.push(code);
                        state.style = state.style || {};
                        codes.forEach(code => {
                            const style = state.style;
                            const codeNumber = parseInt(code);
                            if (fgColors[code]) {
                                style.fg = fgColors[code];
                            }
                            else if (bgColors[code]) {
                                style.bg = bgColors[code];
                            }
                            else if (code === Resets.Fg) {
                                style.fg = "";
                            }
                            else if (code === Resets.Bg) {
                                style.bg = "";
                            }
                            else if (codeNumber >= 91 && codeNumber <= 97) {
                                style.fg = fgColors[codeNumber - 60] + BrightClassPostfix;
                            }
                            else if (codeNumber >= 101 && codeNumber <= 107) {
                                style.bg = bgColors[codeNumber - 60] + BrightClassPostfix;
                            }
                            else if (specials[code]) {
                                style[specials[code]] = true;
                            }
                            else if (code === Resets.Bold) {
                                style.bold = false;
                            }
                            else if (code === Resets.Italic) {
                                style.italic = false;
                            }
                            else if (code === Resets.Underline) {
                                style.underline = false;
                            }
                        });
                        // clear
                        command = "";
                        currentText = "";
                        code = "";
                    }
                    else {
                        isCommandActive = false;
                        command = "";
                    }
                    codes = [];
                }
                else if (isNaN(parseInt(character))) {
                    // if this is not a number, eg: 0K, this isn't something we support
                    code = "";
                    isCommandActive = false;
                    command = "";
                }
                else if (code.length === 4) {
                    // we probably got code that we don't support, ignore
                    code = "";
                    isCommandActive = false;
                    if (character !== ESC) {
                        // if this is not an ESC, let's not consider command from now on
                        // eg: ESC[0Ksometexthere, at this point, code would be 0K, character would be 's'
                        command = "";
                        currentText += character;
                    }
                }
                else {
                    code += character;
                }
                continue;
            }
            else if (command) {
                if (command === ESC && character === "\[") {
                    isCommandActive = true;
                    // push state
                    if (currentText) {
                        state.output = currentText;
                        result.push(state);
                        state = {};
                        currentText = "";
                    }
                }
                continue;
            }
            if (character === ESC) {
                command = character;
            }
            else {
                currentText += character;
            }
        }
        // still pending text
        if (currentText) {
            state.output = currentText + (command ? command : "");
            result.push(state);
        }
        return result;
    }
    _escape(content, skip) {
        if (skip) {
            return content;
        }
        return escape(content);
    }
    /**
     * Wraps the provided string with `<a>` tag to make the link clickable in logs
     * @param link The URL to be wrapped
     * @returns a new string with a wrapped link: `<a class="is-link" href= link>link</a>`
     */
    _wrapLink(link) {
        return `<a class="is-link" href= ${link}>` + link + "</a>";
    }
    _getLink(currentText, dangerouslyDontEscape) {
        const url = currentText.match(_urlRegex);
        if (url) {
            const text = currentText.split(url[0]);
            //beginText will never have a link in it since we are recursively going through and splitting on the links
            const beginText = text[0];
            const escapedLink = this._escape(url[0], !!dangerouslyDontEscape);
            //set endText as the remaining text after url
            const startIndexOfRemainingText = currentText.indexOf(url[0]) + url[0].length;
            let endText = currentText.slice(startIndexOfRemainingText);
            //only continue splitting if we aren't at the end of the line
            if (endText !== newLineChar && endText !== carriageReturnNewLineChar) {
                endText = this._getLink(endText, dangerouslyDontEscape);
            }
            return this._escape(beginText, !!dangerouslyDontEscape) + this._wrapLink(escapedLink) + endText;
        }
        else {
            return this._escape(currentText, !!dangerouslyDontEscape);
        }
    }
}
